/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/16/22
 */

import React from "react";
import {
	Button, Chip, Dialog,
	DialogActions,
	DialogContent, DialogContentText,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Stack,
	Typography
} from "@mui/material";
import {useMutation, useQuery} from "urql";
import {SavedCalendarsQuery, SavedCalendarsQueryVars, SavedCalendarsResponse} from "../graphql/queries/calendars";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../state/store";
import LoadingHero from "../components/LoadingHero";
import HeroMessage from "../components/HeroMessage";
import {Calendar} from "../interfaces/Global";
import {Link} from "react-router-dom";
import DeleteIcon from '@mui/icons-material/Delete';
import {
	DeleteSavedCalendarMutation,
	DeleteSavedCalendarResponse,
	DeleteSavedCalendarVars
} from "../graphql/mutations/calendars";
import {LoadingButton} from "@mui/lab";
import {set} from "../state/reducers/calendar";

export default function MyCalendars() {

	const user = useSelector((s:State) => s.user);
	const defaultCalendarId = useSelector((s:State) => s.defaultCalendarId);

	const [ calendarsResult ] = useQuery<SavedCalendarsResponse, SavedCalendarsQueryVars>({
		query: SavedCalendarsQuery,
		variables: {
			createdBy: user?.id,
			orderBy: "-id"
		}
	});

	const calendars = calendarsResult.data?.savedCalendars.items || [];

	return (
		<Stack spacing={1}>
			<Typography variant={"h5"} sx={{ pb: 2 }}>My Calendars</Typography>
			{calendarsResult.error &&
				<HeroMessage
					p={4}
					title={"Request Error"}
					titleTypographyProps={{
						variant: "h6"
					}}
					message={calendarsResult.error.message}
				/>
			}
			{calendarsResult.fetching &&
				<LoadingHero />
			}
			{!calendarsResult.error && !calendarsResult.fetching && calendars.length === 0 &&
				<HeroMessage
					p={4}
					title={"You don't have any calendars"}
					titleTypographyProps={{
						variant: "h6"
					}}
					message={"Saved calendars will appear here"}
				/>
			}
			{!calendarsResult.error && !calendarsResult.fetching && calendars.length > 0 &&
			<List dense disablePadding>
				{calendars.map(calendar =>
					<SavedCalendarItem
						key={calendar.id}
						calendar={calendar}
						defaultCalendar={defaultCalendarId === calendar.id}
					/>)}
			</List>}
		</Stack>
	);

}

function SavedCalendarItem ({ calendar, defaultCalendar } : { calendar: Calendar, defaultCalendar: boolean }) {

	const dispatch = useDispatch();
	const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = React.useState(false);

	const [deleteResult, deleteCalendar] = useMutation<DeleteSavedCalendarResponse, DeleteSavedCalendarVars>(DeleteSavedCalendarMutation);

	return (
		<ListItem
			disableGutters
			disablePadding
			divider
			secondaryAction={
				<IconButton
					size={"small"}
					edge={"end"}
					onClick={() => setConfirmDeleteDialogOpen(true)}
				>
					<DeleteIcon />
				</IconButton>
			}
		>
			<ListItemButton disableGutters component={Link} to={"/calendar/" + calendar.id}>
				<ListItemText primary={
					<span>
						{calendar.title}
						{defaultCalendar && <Chip sx={{ ml: 1 }} label="Default" size={"small"} />}
					</span>} />
			</ListItemButton>
			<Dialog
				open={confirmDeleteDialogOpen}
				onClose={() => { setConfirmDeleteDialogOpen(false)}}
			>
				<DialogTitle>
					Confirm delete - {calendar.title}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete this calendar? This operation is irreversible.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
					<LoadingButton
						loading={deleteResult.fetching}
						onClick={() => {
							setConfirmDeleteDialogOpen(false);
							deleteCalendar({id: calendar.id}).then(() => {
								if (defaultCalendar) {
									dispatch(set({ id: 0, title: "Deleted Calendar" }));
								}
							});
						}}
						color={"primary"}
						variant={"contained"}
					>
						Delete
					</LoadingButton>
				</DialogActions>
			</Dialog>
		</ListItem>
	);
}